/**
 * FuzzyContextPanel - Phase progress display for Participant Workspace
 *
 * Shows high-level progress context without revealing hidden task details.
 * Displays phase-level progress bars with status counts.
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { colors } from '../../theme/colors';

interface StatusCounts {
  todo: number;
  inProgress: number;
  blocked: number;
  completed: number;
}

interface PhaseContext {
  phaseId: string;
  phaseLabel: string;
  counts: StatusCounts;
}

interface FuzzyPlanContext {
  totals: StatusCounts;
  phases: PhaseContext[];
  totalSteps: number;
  visibleSteps: number;
}

interface FuzzyContextPanelProps {
  fuzzyContext: FuzzyPlanContext;
}

export const FuzzyContextPanel: React.FC<FuzzyContextPanelProps> = ({
  fuzzyContext,
}) => {
  const { totals, phases } = fuzzyContext;

  // Calculate overall progress
  const overallDone = totals.completed;
  const overallTotal = totals.todo + totals.inProgress + totals.blocked + totals.completed;
  const overallPercent = overallTotal > 0 ? (overallDone / overallTotal) * 100 : 0;

  const getPhaseProgress = (counts: StatusCounts) => {
    const total = counts.todo + counts.inProgress + counts.blocked + counts.completed;
    if (total === 0) return 0;
    return (counts.completed / total) * 100;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: colors.gray[50],
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: colors.textSecondary, mb: 2, fontWeight: 600 }}
      >
        Plan Progress
      </Typography>

      {/* Overall progress */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
            Overall
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            {Math.round(overallPercent)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={overallPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: colors.gray[200],
            '& .MuiLinearProgress-bar': {
              bgcolor: colors.green[600],
              borderRadius: 4,
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Tooltip title="Completed">
            <Typography variant="caption" sx={{ color: colors.green[600] }}>
              {totals.completed} done
            </Typography>
          </Tooltip>
          <Tooltip title="In Progress">
            <Typography variant="caption" sx={{ color: colors.blue[600] }}>
              {totals.inProgress} active
            </Typography>
          </Tooltip>
          <Tooltip title="Blocked">
            <Typography variant="caption" sx={{ color: colors.amber[600] }}>
              {totals.blocked} blocked
            </Typography>
          </Tooltip>
          <Tooltip title="Not Started">
            <Typography variant="caption" sx={{ color: colors.gray[500] }}>
              {totals.todo} todo
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {/* Phase breakdown */}
      {phases.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}
          >
            By Phase
          </Typography>
          <Box sx={{ mt: 1 }}>
            {phases.map((phase) => {
              const phasePercent = getPhaseProgress(phase.counts);
              const phaseTotal =
                phase.counts.todo +
                phase.counts.inProgress +
                phase.counts.blocked +
                phase.counts.completed;

              if (phaseTotal === 0) return null;

              return (
                <Box key={phase.phaseId} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textPrimary,
                        fontWeight: 500,
                        maxWidth: '70%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {phase.phaseLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      {phase.counts.completed}/{phaseTotal}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={phasePercent}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: colors.gray[200],
                      '& .MuiLinearProgress-bar': {
                        bgcolor: phasePercent === 100 ? colors.green[600] : colors.blue[500],
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default FuzzyContextPanel;
